/**
 * Card index store - Jotai atoms for preloaded MinimalCard index
 *
 * Uses a minimal card index for fast loading and import matching.
 * Full card data is fetched from Firestore only for collection/deck cards.
 *
 * NOTE: These atoms contain MinimalCard data only (not full ScryfallCard).
 * For full card data, use fullCardCacheAtom from @/state/cardCache.
 */
import type { Color, ImageUris, Legality, Rarity } from '@/types/card';
import { atom } from 'jotai';

/**
 * Formats included in the minimal card index for legality checking
 */
export type IndexFormat = 'standard' | 'pioneer' | 'modern' | 'legacy' | 'vintage' | 'pauper' | 'commander' | 'brawl' | 'oathbreaker';

/**
 * Minimal card data for import matching and deck building
 * Includes fields for filtering and validation without full Scryfall data
 *
 * NOTE: This must match the fields generated by functions/src/scryfall/generateIndex.ts
 * Do not add fields here that aren't in the backend index generator.
 */
export interface MinimalCard {
  id: string;
  oracle_id: string | null;
  name: string;
  name_lower: string;
  set: string;
  collector_number: string;
  image_uris: Pick<ImageUris, 'small' | 'normal'> | null;
  // Extended fields for filtering and validation
  type_line: string | null;
  color_identity: Color[];
  cmc: number | null;
  legalities: Partial<Record<IndexFormat, Legality>>;
  rarity: Rarity;
}

// Raw card array - populated on app startup from CDN/IndexedDB
export const cardIndexAtom = atom<MinimalCard[]>([]);

// Loading state
export const cardIndexLoadingAtom = atom<boolean>(true);

// Loading progress for UI feedback
export const cardIndexProgressAtom = atom<string>('Initializing...');

// Error state
export const cardIndexErrorAtom = atom<string | null>(null);

// Derived: Map by Scryfall ID for O(1) lookup
export const cardIndexByIdAtom = atom<Map<string, MinimalCard>>((get) => {
  const cards = get(cardIndexAtom);
  return new Map(cards.map((card) => [card.id, card]));
});

// Derived: Map by set+collector_number for import matching
export const cardIndexBySetNumberAtom = atom<Map<string, MinimalCard>>((get) => {
  const cards = get(cardIndexAtom);
  const map = new Map<string, MinimalCard>();
  for (const card of cards) {
    const key = `${card.set.toLowerCase()}:${card.collector_number}`;
    map.set(key, card);
  }
  return map;
});

// Derived: Map by lowercase name for exact name matching
// Includes back-face names from DFC/split/adventure cards (after " // ")
export const cardIndexByNameAtom = atom<Map<string, MinimalCard>>((get) => {
  const cards = get(cardIndexAtom);
  const map = new Map<string, MinimalCard>();
  for (const card of cards) {
    // Only store first occurrence (for duplicates, prefer earliest)
    if (!map.has(card.name_lower)) {
      map.set(card.name_lower, card);
    }
    // Index back-face name for DFC/split/adventure cards
    const separatorIdx = card.name_lower.indexOf(' // ');
    if (separatorIdx !== -1) {
      const backFaceName = card.name_lower.slice(separatorIdx + 4);
      if (backFaceName && !map.has(backFaceName)) {
        map.set(backFaceName, card);
      }
    }
  }
  return map;
});

// Derived: Sorted name array for fuzzy prefix search
// Deduplicates by oracle_id so different cards with the same name (e.g. regular vs art series) both appear
// Includes back-face names from DFC/split/adventure cards (after " // ")
export const sortedCardIndexNamesAtom = atom<{ name: string; card: MinimalCard }[]>((get) => {
  const cards = get(cardIndexAtom);
  const entries: { name: string; card: MinimalCard }[] = [];
  const seen = new Set<string>();
  for (const card of cards) {
    // Skip cards with no oracle_id (e.g. reversible_card layout without face fallback)
    if (!card.oracle_id) continue;
    // Key by oracle_id so cards with the same name but different oracle_ids both appear
    if (!seen.has(card.oracle_id)) {
      seen.add(card.oracle_id);
      entries.push({ name: card.name_lower, card });
    }
    // Index back-face name for DFC/split/adventure cards
    const separatorIdx = card.name_lower.indexOf(' // ');
    if (separatorIdx !== -1) {
      const backFaceName = card.name_lower.slice(separatorIdx + 4);
      if (backFaceName) {
        const backKey = `back:${backFaceName}:${card.oracle_id}`;
        if (!seen.has(backKey)) {
          seen.add(backKey);
          entries.push({ name: backFaceName, card });
        }
      }
    }
  }
  return entries.sort((a, b) => a.name.localeCompare(b.name));
});

// Derived: Map by set code for debugging (list all cards in a set)
export const cardIndexBySetAtom = atom<Map<string, MinimalCard[]>>((get) => {
  const cards = get(cardIndexAtom);
  const map = new Map<string, MinimalCard[]>();
  for (const card of cards) {
    const setKey = card.set.toLowerCase();
    const existing = map.get(setKey);
    if (existing) {
      existing.push(card);
    } else {
      map.set(setKey, [card]);
    }
  }
  return map;
});

// Stats atom for debugging/display
export const cardIndexStatsAtom = atom((get) => {
  const cards = get(cardIndexAtom);
  const loading = get(cardIndexLoadingAtom);
  return {
    totalCards: cards.length,
    loading,
    loaded: !loading && cards.length > 0,
  };
});
